/**
 * FieldPartnerDashboard — Field/Channel Partner (external IC).
 *
 * Ported from `FieldPartnerDashboard` in aura-exec.jsx.
 * My assigned installations + KYC status chip + claim submission + payout
 * ledger + incentive tier + fraud-flag dispute view.
 *
 * UI-only. Tenant-safe.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Users } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { safeGet, formatINRCompact, formatNum, todayLabel, formatDateShort } from './_execShared';

// ─── Types ────────────────────────────────────────────────────────────────

interface Installation {
  id: string;
  code?: string;
  customerName?: string;
  customer?: { name?: string };
  address?: string;
  productType?: string;
  product?: string;
  status?: string;
  step?: number;
  scheduledDate?: string;
}

interface Claim {
  id: string;
  number?: string;
  unit?: string;
  againstInstallation?: string;
  amount?: number;
  status?: 'Pending' | 'Approved' | 'Rejected' | 'Disputed' | string;
  agingDays?: number;
  fraudFlag?: boolean;
  disputeReason?: string;
}

interface PayoutEntry {
  id: string;
  date?: string;
  reason?: string;
  reference?: string;
  amount?: number;
  paid?: boolean;
  status?: 'paid' | 'pending' | 'rejected' | string;
}

interface KycStatus {
  status?: 'verified' | 'pending' | 'rejected' | string;
  tier?: string;
}

interface IncentivePayload {
  earned?: number;
  target?: number;
  tier?: string;
  nextTier?: string;
}

// ─── Atoms ────────────────────────────────────────────────────────────────

function Skeleton({ h = 60 }: { h?: number }) {
  return (
    <div
      style={{
        height: h,
        background: 'var(--ivory-2)',
        borderRadius: 6,
        animation: 'pulse 1.5s ease-in-out infinite',
      }}
    />
  );
}

function NoData({ label = 'No data yet' }: { label?: string }) {
  return <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: '8px 0' }}>{label}</div>;
}

function Chip({
  children,
  color = 'var(--ink-3)',
  bg = 'var(--ivory-2)',
}: {
  children: React.ReactNode;
  color?: string;
  bg?: string;
}) {
  return (
    <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 10, background: bg, color, fontWeight: 500 }}>
      {children}
    </span>
  );
}

function KpiCard({
  label,
  value,
  subtitle,
  color,
}: {
  label: string;
  value: string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="kpi-label">{label}</div>
      <div className="num-display" style={{ fontSize: 22, marginTop: 2, color }}>
        {value}
      </div>
      {subtitle && <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>{subtitle}</div>}
    </div>
  );
}

function IncentiveBar({ data }: { data: IncentivePayload | null }) {
  if (!data || !data.target) {
    return (
      <div
        className="card"
        style={{
          padding: 14,
          background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
          border: '1px solid var(--saffron-100)',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--saffron-600)',
            textTransform: 'uppercase',
            letterSpacing: 0.08,
            fontWeight: 600,
          }}
        >
          Incentive tier
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 6 }}>
          No incentive plan configured
        </div>
      </div>
    );
  }
  const earned = data.earned ?? 0;
  const target = data.target ?? 1;
  const pct = Math.min(100, (earned / target) * 100);
  const diff = Math.max(0, target - earned);

  return (
    <div
      className="card"
      style={{
        padding: 16,
        background: 'linear-gradient(180deg, var(--saffron-50), var(--saffron-100))',
        border: '1px solid var(--saffron-100)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 10 }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: 'var(--saffron-600)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              fontWeight: 600,
            }}
          >
            {data.tier ?? 'Bronze'} tier · this month
          </div>
          <div className="num-display" style={{ fontSize: 24, marginTop: 4 }}>
            {formatINRCompact(earned)}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>of {formatINRCompact(target)}</div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 11, color: 'var(--ink-2)', textAlign: 'right' }}>
          Next: <strong style={{ color: 'var(--saffron-600)' }}>{data.nextTier ?? '—'}</strong>
          <div style={{ fontSize: 10, marginTop: 2 }}>{formatINRCompact(diff)} to go</div>
        </div>
      </div>
      <div
        style={{
          height: 6,
          background: 'color-mix(in srgb, var(--ivory-0) 50%, transparent)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <div style={{ width: `${pct}%`, height: '100%', background: 'var(--saffron)' }} />
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────

export function FieldPartnerDashboard({
  onSeeTeam,
}: {
  onSeeTeam?: () => void;
} = {}) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const firstName = user?.firstName ?? 'there';

  const [installs, setInstalls] = useState<Installation[]>([]);
  const [installsLoading, setInstallsLoading] = useState(true);
  const [installsErr, setInstallsErr] = useState<string | null>(null);

  const [claims, setClaims] = useState<Claim[]>([]);
  const [claimsLoading, setClaimsLoading] = useState(true);

  const [payouts, setPayouts] = useState<PayoutEntry[]>([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);

  const [kyc, setKyc] = useState<KycStatus | null>(null);
  const [incentive, setIncentive] = useState<IncentivePayload | null>(null);

  useEffect(() => {
    safeGet<{ items?: Installation[] } | Installation[]>(
      '/field-partners/me/installations',
    ).then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw)
        ? raw
        : (raw as { items?: Installation[] } | null)?.items ?? [];
      setInstalls(parsed);
      setInstallsErr(r.error);
      setInstallsLoading(false);
    });

    safeGet<{ items?: Claim[] } | Claim[]>('/field-partners/me/claims').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw) ? raw : (raw as { items?: Claim[] } | null)?.items ?? [];
      setClaims(parsed);
      setClaimsLoading(false);
    });

    safeGet<{ items?: PayoutEntry[] } | PayoutEntry[]>('/field-partners/me/payouts').then((r) => {
      const raw = r.data;
      const parsed = Array.isArray(raw)
        ? raw
        : (raw as { items?: PayoutEntry[] } | null)?.items ?? [];
      setPayouts(parsed);
      setPayoutsLoading(false);
    });

    safeGet<KycStatus>('/field-partners/me/kyc-status').then((r) => setKyc(r.data));

    safeGet<IncentivePayload>('/field-partners/me/incentive-progress').then((r) =>
      setIncentive(r.data),
    );
  }, []);

  const getCustomer = (i: Installation) => i.customerName ?? i.customer?.name ?? '—';
  const getProduct = (i: Installation) => i.productType ?? i.product ?? '—';

  const disputed = claims.filter((c) => c.fraudFlag || c.status === 'Disputed');
  const installsMtd = installs.length;
  const totalPaid = payouts.filter((p) => p.paid || p.status === 'paid').reduce((s, p) => s + (p.amount ?? 0), 0);
  const totalPending = payouts.filter((p) => !(p.paid || p.status === 'paid')).reduce((s, p) => s + (p.amount ?? 0), 0);

  const kycColor =
    kyc?.status === 'verified'
      ? 'var(--forest-600)'
      : kyc?.status === 'rejected'
      ? 'var(--danger, #B8563B)'
      : 'var(--saffron-600)';

  return (
    <div
      className="aura-root"
      style={{ padding: 20, background: 'var(--ivory-0)', minHeight: '100%' }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-end', marginBottom: 14, gap: 12 }}>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 0.08,
              marginBottom: 3,
            }}
          >
            Field Partner
          </div>
          <h2 style={{ fontFamily: 'var(--ff-serif)', fontSize: 24, fontWeight: 500 }}>
            Namaste, {firstName}
          </h2>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>{todayLabel()}</span>
            {kyc && (
              <>
                <span>·</span>
                <Chip color={kycColor} bg="var(--ivory-2)">
                  KYC {kyc.status ?? 'pending'}
                </Chip>
              </>
            )}
            {incentive?.tier && (
              <>
                <span>·</span>
                <Chip color="var(--saffron-600)">{incentive.tier} tier</Chip>
              </>
            )}
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {onSeeTeam && (
          <button
            className="btn btn-outline btn-sm"
            onClick={onSeeTeam}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <Users size={12} /> See channel view
          </button>
        )}
      </div>

      {/* KPI strip */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 10,
          marginBottom: 14,
        }}
      >
        <KpiCard
          label="Installs MTD"
          value={formatNum(installsMtd)}
          subtitle={incentive?.tier ? `${incentive.tier} tier` : undefined}
          color={installsMtd > 0 ? 'var(--forest-600)' : undefined}
        />
        <KpiCard
          label="Payout earned"
          value={formatINRCompact(totalPaid)}
          subtitle="Settled this cycle"
          color="var(--saffron-600)"
        />
        <KpiCard label="Payout pending" value={formatINRCompact(totalPending)} subtitle="In queue" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 14 }}>
        <div>
          {/* My installations */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>My installations</h3>
            <div style={{ flex: 1 }} />
            {/* Apr 27 (audit P1) — Start new install navigates to ITBP installations new. */}
            <button
              className="btn btn-sm"
              onClick={() => navigate('/service/itbp-installations?action=new')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 4,
                background: 'var(--ink-0)',
                color: 'var(--ivory-0)',
                border: '1px solid var(--ink-0)',
              }}
            >
              <Plus size={11} /> Start new install
            </button>
          </div>
          {installsLoading ? (
            <Skeleton h={160} />
          ) : installsErr ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="Installations unavailable" />
            </div>
          ) : installs.length === 0 ? (
            <div className="card" style={{ padding: 14, marginBottom: 18 }}>
              <NoData label="No installations assigned to you yet" />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 18 }}>
              {installs.slice(0, 8).map((i) => {
                const status = i.status ?? '—';
                const statusColor = status.toLowerCase().includes('activat')
                  ? 'var(--forest-600)'
                  : status.toLowerCase().includes('progress')
                  ? 'var(--saffron-600)'
                  : 'var(--ink-3)';
                return (
                  <div
                    key={i.id}
                    className="card"
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10 }}
                  >
                    <div
                      className="num"
                      style={{
                        width: 44,
                        height: 36,
                        borderRadius: 6,
                        background: 'var(--ivory-2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        color: 'var(--ink-3)',
                        flexShrink: 0,
                      }}
                    >
                      {(i.code ?? i.id).slice(-4)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500 }}>
                        {getCustomer(i)}
                        <span style={{ fontWeight: 400, color: 'var(--ink-3)' }}>
                          {' '}
                          · {getProduct(i)}
                        </span>
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: 'var(--ink-3)',
                          display: 'flex',
                          gap: 6,
                          marginTop: 2,
                        }}
                      >
                        {i.address && (
                          <>
                            <span>{i.address}</span>
                            <span>·</span>
                          </>
                        )}
                        <span style={{ color: statusColor, fontWeight: 500 }}>● {status}</span>
                      </div>
                    </div>
                    {/* Apr 27 (audit P0/P1) — wired Open + Claim to install detail + claims pages. */}
                    <button
                      className="btn btn-sm btn-outline"
                      onClick={() => navigate(`/service/itbp-installations/${i.id}`)}
                    >
                      Open
                    </button>
                    <button
                      className="btn btn-sm"
                      onClick={() => navigate(`/field-partners/claims?install=${encodeURIComponent(i.id)}`)}
                      style={{
                        background: 'var(--ink-0)',
                        color: 'var(--ivory-0)',
                        border: '1px solid var(--ink-0)',
                      }}
                    >
                      Claim
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* My claims */}
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500 }}>My claims</h3>
            <span style={{ marginLeft: 8 }}>
              <Chip>{claims.length}</Chip>
            </span>
          </div>
          {claimsLoading ? (
            <Skeleton h={100} />
          ) : claims.length === 0 ? (
            <div className="card" style={{ padding: 14 }}>
              <NoData label="No claims submitted" />
            </div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: 'var(--ivory-1)', color: 'var(--ink-3)' }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>Claim</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>
                      Against
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      Amount
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 500 }}>
                      Aging
                    </th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 500 }}>
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {claims.map((c) => {
                    const st = (c.status ?? 'Pending').toString();
                    const color =
                      st.includes('Approved')
                        ? 'var(--forest-600)'
                        : st.includes('Disputed') || st.includes('Rejected') || c.fraudFlag
                        ? 'var(--danger, #B8563B)'
                        : 'var(--saffron-600)';
                    return (
                      <tr key={c.id} style={{ borderTop: '1px solid var(--line)' }}>
                        <td className="num" style={{ padding: '8px 10px' }}>
                          {c.number ?? c.id}
                        </td>
                        <td style={{ padding: '8px 10px' }}>
                          {c.unit ?? c.againstInstallation ?? '—'}
                        </td>
                        <td className="num" style={{ padding: '8px 10px', textAlign: 'right' }}>
                          {formatINRCompact(c.amount ?? 0)}
                        </td>
                        <td
                          className="num"
                          style={{ padding: '8px 10px', textAlign: 'right', color: 'var(--ink-3)' }}
                        >
                          {c.agingDays != null ? `${c.agingDays}d` : '—'}
                        </td>
                        <td
                          style={{
                            padding: '8px 10px',
                            fontSize: 11,
                            fontWeight: 500,
                            color,
                          }}
                        >
                          ● {st}
                          {c.fraudFlag && ' · fraud flag'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <IncentiveBar data={incentive} />

          <div className="card" style={{ padding: 14 }}>
            <div className="kpi-label" style={{ marginBottom: 10 }}>
              Payout ledger
            </div>
            {payoutsLoading ? (
              <Skeleton h={100} />
            ) : payouts.length === 0 ? (
              <NoData label="No payouts yet" />
            ) : (
              payouts.slice(0, 6).map((e, i) => {
                const paid = e.paid || e.status === 'paid';
                return (
                  <div
                    key={e.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 0',
                      borderBottom: i < Math.min(payouts.length, 6) - 1 ? '1px solid var(--line)' : 'none',
                    }}
                  >
                    <div
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 99,
                        background: paid ? 'var(--forest-600)' : 'var(--ink-3)',
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12 }}>{e.reason ?? e.reference ?? 'Payout'}</div>
                      <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                        {formatDateShort(e.date)}
                      </div>
                    </div>
                    <div
                      className="num"
                      style={{ fontSize: 12, color: paid ? 'var(--forest-600)' : 'var(--ink-2)' }}
                    >
                      {formatINRCompact(e.amount ?? 0)}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Fraud dispute card */}
          {disputed.length > 0 && (
            <div className="card" style={{ padding: 14, borderLeft: '3px solid var(--danger, #B8563B)' }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: 'var(--danger, #B8563B)',
                  marginBottom: 6,
                }}
              >
                ⚠ {disputed.length} claim{disputed.length === 1 ? '' : 's'} under dispute
              </div>
              <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>
                {disputed[0].disputeReason ??
                  'Fraud flag raised. Upload additional photo evidence to resolve.'}
              </div>
              {/* Apr 27 (audit P1) — wire to fraud-review page with disputed claim id. */}
              <button
                className="btn btn-sm"
                onClick={() => navigate(`/field-partners/fraud-review?claim=${encodeURIComponent(disputed[0].id)}`)}
                style={{ marginTop: 8, width: '100%' }}
              >
                Submit evidence
              </button>
            </div>
          )}
        </div>
      </div>

      <style>{`@keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.5; } }`}</style>
    </div>
  );
}

export default FieldPartnerDashboard;
